// ---------------------------------------------------------------------------
// resolveLeadTreatmentPrice
//
// Single source of truth for the price cascade displayed in attended-popup
// price headers.  Shared by ConsultationAttendedAppointment and
// AttendedAppointment (both previously had inline copies).
//
// When a specific treatment is selected (multi-treatment popups), resolves THAT
// treatment's fresh price by matching its id within followLead — prevents a
// recently-edited different treatment's price bleeding in.  Falls back to the
// status-sorted pick when no specific treatment is selected.
//
// Price cascade (per-lead override first, then master defaults):
//   custom_price → treatment_data.price → treatment_item.price → null
//
// Consistent with TreatmentPlanCostTable.jsx:36, TreatmentAndAppointment.jsx:363.
// ---------------------------------------------------------------------------

/**
 * @param {object} opts
 * @param {object} [opts.followLead]          - Fresh server-side lead object (preferred)
 * @param {object} [opts.selectedTreatmentData] - Click-time treatment snapshot; when its id
 *                                               is present the resolver matches by id first.
 * @param {string} [opts.filterStatus]        - Treatment status to prefer (default: CONSULTATION_ATTENDED)
 * @returns {number|null} The resolved price, or null if no price data found.
 */
export default function resolveLeadTreatmentPrice({
    followLead,
    selectedTreatmentData,
    filterStatus = 'CONSULTATION_ATTENDED',
} = {}) {
    const allTx = followLead?.lead_treatments || [];
    const ltSelected = selectedTreatmentData?.treatmentData;

    // When a specific treatment is selected (multi-treatment popups), resolve THAT
    // treatment's price — match the fresh followLead row by id so an edit to a
    // different treatment can't bleed its price in. Falls through if not found.
    if (ltSelected?.id != null) {
        const fresh = allTx.find((t) => String(t.id) === String(ltSelected.id));
        const selectedPrice = fresh?.custom_price ?? fresh?.treatment_data?.price ?? fresh?.treatment_item?.price
            ?? ltSelected?.custom_price ?? ltSelected?.price ?? null;
        if (selectedPrice != null) return selectedPrice;
    }

    // Fallback (no specific selection): status-filtered, most-recently-updated, then [0].
    const treatments = allTx.filter((t) => t.status === filterStatus);
    const sorted = [...treatments].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    const lt = sorted[0] || allTx[0];
    return lt?.custom_price ?? lt?.treatment_data?.price ?? lt?.treatment_item?.price ?? null;
}
